import styled, { css } from "styled-components";
import { Textload } from "../utils/Skeleton.styles";
import type { TextVariantsType } from "./Text";
import type { UscColorType, UscSpacingType } from "../Variables.types";
import { UscColorProps, UscSpacingProps } from "../Variables.types";
import { breakpoints } from "../../../../shared-logic/themes/lexusTheme";

// STYLES
export type SpacingType = {
    m?: UscSpacingType;
    mt?: UscSpacingType;
    mb?: UscSpacingType;
    ml?: UscSpacingType;
    mr?: UscSpacingType;
    mx?: UscSpacingType;
    my?: UscSpacingType;
};

const globalStyle = css<{ color?: UscColorType } & SpacingType>`
    --_font-family: var(--font-family, var(--font-regular));
    --_text-transform: var(--text-transform, uppercase);
    --_text-color: var(--text-color, var(--color-midnight));

    margin-block: 0;
    font-family: var(--_font-family);
    text-transform: var(--_text-transform);
    color: var(--_text-color);

    ${({ color }) =>
        color &&
        css`
            color: var(${UscColorProps[color]});
        `};

    ${(props) =>
        props.m &&
        css`
            margin: var(${UscSpacingProps[props.m]});
        `}
    ${(props) =>
        props.mt &&
        css`
            margin-top: var(${UscSpacingProps[props.mt]});
        `}
    ${(props) =>
        props.mb &&
        css`
            margin-bottom: var(${UscSpacingProps[props.mb]}) !important;
        `}
    ${(props) =>
        props.ml &&
        css`
            margin-inline-start: var(${UscSpacingProps[props.ml]});
        `}
    ${(props) =>
        props.mr &&
        css`
            margin-inline-end: var(${UscSpacingProps[props.mr]});
        `}
    ${(props) =>
        props.mx &&
        css`
            margin-inline: var(${UscSpacingProps[props.mx]});
        `}
    ${(props) =>
        props.my &&
        css`
            margin-block: var(${UscSpacingProps[props.my]});
        `}
`;

const h1Style = css`
    ${globalStyle};
    font-size: 3.2rem;
    letter-spacing: 0.2rem;
    line-height: 4rem;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 4.8rem;
        line-height: 6rem;
    }
`;

const h1SmallStyle = css`
    ${globalStyle};
    font-size: 3.2rem;
    letter-spacing: 0.2rem;
    line-height: 4rem;
`;

const h2Style = css`
    ${globalStyle};
    font-size: 2.8rem;
    letter-spacing: 0.2rem;
    line-height: 3.6rem;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 4rem;
        line-height: 5rem;
    }
`;

const h2SmallStyle = css`
    ${globalStyle};
    font-size: 2.8rem;
    letter-spacing: 0.2rem;
    line-height: 3.6rem;
`;

const h3Style = css`
    ${globalStyle};
    font-size: 2.4rem;
    letter-spacing: 0.15rem;
    line-height: 3.2rem;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 3.2rem;
        line-height: 4rem;
    }
`;

const h3SmallStyle = css`
    ${globalStyle};
    font-size: 2.4rem;
    letter-spacing: 0.15rem;
    line-height: 3.2rem;
`;

const h4Style = css`
    ${globalStyle};
    font-size: 2rem;
    letter-spacing: 0.15rem;
    line-height: 2.8rem;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 2.8rem;
        line-height: 3rem;
    }
`;

const h4SmallStyle = css`
    ${globalStyle};
    font-size: 2rem;
    letter-spacing: 0.15rem;
    line-height: 2.8rem;
`;

const h5Style = css`
    ${globalStyle};
    font-size: 1.4rem;
    letter-spacing: 0.3rem;
    line-height: 2.2rem;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 1.6rem;
    }
`;

const h5SmallStyle = css`
    ${globalStyle};
    font-size: 1.4rem;
    letter-spacing: 0.2rem;
    line-height: 2.2rem;
`;

const h6Style = css`
    ${globalStyle};
    font-size: 1.2rem;
    letter-spacing: 0.2rem;
    line-height: 2rem;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 1.4rem;
    }
`;

const h6SmallStyle = css`
    ${globalStyle};
    font-size: 1.2rem;
    letter-spacing: 0.2rem;
    line-height: 2rem;
`;

const h6StrikethroughStyle = css`
    ${globalStyle};
    font-size: 1.2rem;
    letter-spacing: 0.2rem;
    line-height: 2rem;
    text-decoration-line: strikethrough;

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 1.4rem;
    }
`;

const h6SmallStrikethroughStyle = css`
    ${globalStyle};
    font-size: 1.2rem;
    letter-spacing: 0.2rem;
    line-height: 2rem;
    text-decoration-line: strikethrough;
`;

const quoteStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    --text-transform: none;
    font-size: 2.4rem;
    letter-spacing: 0.1rem;
    line-height: 3.6rem;
`;

const ctaStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-bold);
    font-size: 1.4rem;
    letter-spacing: 0.1rem;
    line-height: 2rem;
`;

const ctaSmallStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    font-size: 1rem;
    letter-spacing: 0.1rem;
    line-height: 1.4rem;
`;

const captionStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    font-size: 1.4rem;
    letter-spacing: 0.1rem;
    line-height: 2rem;
`;

const bodyBookStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    --text-transform: none;
    font-size: 1.8rem;
    letter-spacing: 0.1rem;
    line-height: 3rem;
`;

const bodyRegularStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --text-transform: none;
    font-size: 1.8rem;
    letter-spacing: 0.1rem;
    line-height: 2rem;
`;

const listStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    --text-transform: none;
    font-size: 1.6rem;
    letter-spacing: 0.1rem;
    line-height: 2rem;
`;

const smallBookStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    --text-transform: none;
    font-size: 1.4rem;
    letter-spacing: 0.014rem;
    line-height: 2rem;
`;

const smallRegularStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --text-transform: none;
    font-size: 1.4rem;
    letter-spacing: 0.014rem;
    line-height: 2rem;
`;

const smallRegularUppercaseStyle = css`
    ${globalStyle};
    font-size: 1.4rem;
    letter-spacing: 0.014rem;
    line-height: 2rem;
`;

const legalStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    --text-transform: none;
    font-size: 1.2rem;
    letter-spacing: 0.5rem;
    line-height: 1.8rem;
`;

const inputLabelStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    --text-transform: none;
    font-size: 1rem;
    line-height: 1.4rem;
`;

// COMPONENT
export const TextVariants = {
    h1: h1Style,
    "h1-small": h1SmallStyle,
    h2: h2Style,
    "h2-small": h2SmallStyle,
    h3: h3Style,
    "h3-small": h3SmallStyle,
    h4: h4Style,
    "h4-small": h4SmallStyle,
    h5: h5Style,
    "h5-small": h5SmallStyle,
    h6: h6Style,
    "h6-small": h6SmallStyle,
    "h6-strikethrough": h6StrikethroughStyle,
    "h6-small-strikethrough": h6SmallStrikethroughStyle,
    quote: quoteStyle,
    cta: ctaStyle,
    caption: captionStyle,
    "cta-small": ctaSmallStyle,
    "paragraph-body-book": bodyBookStyle,
    "paragraph-body-regular": bodyRegularStyle,
    "paragraph-list": listStyle,
    "paragraph-small-book": smallBookStyle,
    "paragraph-small-regular": smallRegularStyle,
    "paragraph-small-regular-uppercase": smallRegularUppercaseStyle,
    "paragraph-legal": legalStyle,
    "paragraph-input-label": inputLabelStyle,
};

const TextVariant = styled.span<{
    variant: TextVariantsType;
    mobileVariant?: TextVariantsType;
    color?: UscColorType;
    spacing?: SpacingType;
    center?: boolean;
    skeleton?: boolean;
}>`
    ${({ skeleton, color }) =>
        skeleton
            ? css`
                  ${Textload};
                  width: 100%;
                  height: 1em;
              `
            : css`
                  ${color &&
                  css`
                      color: var(${UscColorProps[color]});
                  `}
              `}
    ${({ mobileVariant, variant }) =>
        mobileVariant
            ? css`
                  ${TextVariants[mobileVariant]};

                  @media screen and (min-width: 1440px) {
                      ${TextVariants[variant]};
                  }
              `
            : css`
                  ${TextVariants[variant]};
              `}
    
    ${(props) =>
        props.spacing &&
        props.spacing.m &&
        css`
            margin: var(${UscSpacingProps[props.spacing.m]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mt &&
        css`
            margin-top: var(${UscSpacingProps[props.spacing.mt]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mb &&
        css`
            margin-bottom: var(${UscSpacingProps[props.spacing.mb]}) !important;
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.ml &&
        css`
            margin-inline-start: var(${UscSpacingProps[props.spacing.ml]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mr &&
        css`
            margin-inline-end: var(${UscSpacingProps[props.spacing.mr]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mx &&
        css`
            margin-inline: var(${UscSpacingProps[props.spacing.mx]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.my &&
        css`
            margin-block: var(${UscSpacingProps[props.spacing.my]});
        `}
    ${(props) =>
        props.center &&
        css`
            text-align: center;
        `};
`;

export default TextVariant;
